/**
 * The Privacy Policy (docs/refactor/65), in the same shape as the Terms of Use: structured text in
 * public/legal/privacy.json, fetched when somebody opens /privacy, with its own dated version. The
 * same text is in docs/legal/politica-de-privacidade.md for the owner and their lawyer; change both
 * together, and move PRIVACY_VERSION and the dates in privacy.json to the same date.
 *
 * The bracketed placeholders — [RAZÃO SOCIAL], [CNPJ], [ENDEREÇO], [NOME DO ENCARREGADO] — are for
 * the owner to fill in before the text is relied on.
 */

import { useQuery } from '@tanstack/react-query'
import type { Locale } from './i18n'
import type { TermsDocuments } from './termsOfUse'

export const PRIVACY_VERSION = '2026-11-01'

/** Where the text is served from: next to index.html, on the web and in the desktop app alike. */
export const PRIVACY_URL = '/legal/privacy.json'

/** The address the app stores and the phone's listing name for "delete your account" (Google Play asks for one). */
export const DELETE_ACCOUNT_SECTION = '/privacy#excluir-conta'

async function fetchPrivacy(): Promise<TermsDocuments> {
  const response = await fetch(PRIVACY_URL, { cache: 'no-cache' })
  if (!response.ok) throw new Error(`privacy: ${response.status}`)
  return (await response.json()) as TermsDocuments
}

/** The policy in the reader's language: Portuguese for pt-BR, English otherwise. Undefined while loading. */
export function usePrivacyDocument(locale: Locale) {
  const query = useQuery({ queryKey: ['privacy', PRIVACY_VERSION], queryFn: fetchPrivacy, staleTime: Infinity, retry: 2 })
  return { document: query.data?.[locale === 'pt-BR' ? 'pt-BR' : 'en-US'], failed: query.isError }
}
